// src/pages/Cart.js
import React, { useContext, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CartContext } from "../context/CartContext";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../AuthContext"; // 👈 NEW: to get currentUser

// helper: flatten charms object {nailId: [{src,...}, ...]} → array
function flattenCharms(charmsObj = {}) {
  return Object.values(charmsObj).flat() || [];
}

// helper: count charms per nail (for a compact summary)
function countCharmsByNail(charmsObj = {}) {
  const entries = Object.entries(charmsObj).filter(
    ([, arr]) => Array.isArray(arr) && arr.length
  );
  if (!entries.length) return "None";
  return entries
    .map(([nailId, arr]) => {
      const name = nailId
        .replace(/^(left|right)-/, "")
        .replace(/-nail$/, "")
        .replace(/-/g, " ");
      return `${name}: ${arr.length}`;
    })
    .join(", ");
}

// date helpers
function toYMD(date) {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// simple formatter for time labels
function formatTimeLabel(hhmm) {
  const [hStr, mStr] = hhmm.split(":");
  let h = Number(hStr);
  const suffix = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h = h - 12;
  return `${h}:${mStr} ${suffix}`;
}

export default function Cart() {
  const { cart, removeFromCart, clearCart } = useContext(CartContext);

  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth(); // 👈 who placed the order

  // totals
  const totals = useMemo(() => {
    const items = cart ?? [];
    const subtotal = items.reduce(
      (sum, item) => sum + (Number(item.price) || 0),
      0
    );
    const charmUnits = items.reduce(
      (cnt, item) => cnt + flattenCharms(item.charms).length,
      0
    );
    return { subtotal, charmUnits };
  }, [cart]);

  const currency = (n) => (Number.isFinite(n) ? n.toFixed(2) : "0.00");

  // 🔹 payment mode – "deposit" (50%) or "full"
  const [paymentMode, setPaymentMode] = useState("deposit"); // 'deposit' | 'full'

  // deposit vs remaining based on mode
  const depositRate = paymentMode === "deposit" ? 0.5 : 1.0;
  const depositAmount = totals.subtotal * depositRate;
  const remainingAmount = totals.subtotal - depositAmount;

  // pickup state
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccessMsg, setPaymentSuccessMsg] = useState("");

  // confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState(null);

  // earliest pickup date = today + 7 days
  const minPickupDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return toYMD(d);
  }, []);

  // ⏱ quick time options (10:00–18:00, half-hour)
  const timeOptions = [
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
  ];

  async function handlePayDeposit() {
    setBookingError("");
    setPaymentError("");
    setPaymentSuccessMsg("");

    if (!cart || cart.length === 0) {
      setBookingError("Your cart is empty.");
      return;
    }
    if (!pickupDate) {
      setBookingError("Please select a pickup date.");
      return;
    }
    if (!pickupTime) {
      setBookingError("Please select a pickup time.");
      return;
    }
    if (pickupDate < minPickupDate) {
      setBookingError(`Pickup date must be on or after ${minPickupDate}.`);
      return;
    }

    if (!stripe || !elements) {
      setPaymentError("Payment system is still loading. Please try again.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError("Could not find card input.");
      return;
    }

    const depositCents = Math.round(depositAmount * 100);
    if (depositCents <= 0) {
      setPaymentError("Payment amount must be greater than 0.");
      return;
    }

    setIsPaying(true);
    try {
      // 1) Call Cloud Function to create PaymentIntent
      const createDepositIntent = httpsCallable(functions, "createDepositIntent");
      const res = await createDepositIntent({
        amount: depositCents,
        currency: "usd",
        pickupDate,
        pickupTime,
        items: cart.map((item, idx) => ({
          index: idx,
          name: item.name || `${item.shape} Nails`,
          price: Number(item.price) || 0,
          img: item.img || null, // 👈 for backend record
          shape: item.shape || null,
          length: item.length || null,
          color: item.color || null,
          charmsCount: flattenCharms(item.charms).length || 0,
        })),
        paymentMode, // 'deposit' or 'full'
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || null,
      });

      const clientSecret = res?.data?.clientSecret;
      if (!clientSecret) {
        throw new Error("No clientSecret returned from backend.");
      }

      // 2) Confirm card payment with Stripe.js
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (error) {
        console.error("Stripe error:", error);
        setPaymentError(error.message || "Payment failed. Please try again.");
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        setPaymentSuccessMsg(
          paymentMode === "deposit"
            ? "50% deposit paid successfully! 🎉"
            : "Full payment completed! 🎉"
        );

        // 3) Save order in Firestore (with images + user info)
        try {
          await addDoc(collection(db, "orders"), {
            userId: currentUser?.uid || null,
            userEmail: currentUser?.email || null,
            items: cart.map((item) => ({
              name: item.name || `${item.shape} Nails`,
              price: Number(item.price) || 0,
              img: item.img || null, // 👈 used in AdminOrders thumbnails
              shape: item.shape || null,
              length: item.length || null,
              color: item.color || null,
              charmsCount: flattenCharms(item.charms).length || 0,
            })),
            subtotal: totals.subtotal,
            depositAmount,
            remainingAmount,
            pickupDate,
            pickupTime,
            paymentMode, // 'deposit' or 'full'
            stripePaymentIntentId: paymentIntent.id,
            status:
              paymentMode === "deposit" ? "deposit-paid" : "paid-in-full",
            createdAt: serverTimestamp(),
          });
        } catch (err) {
          console.error("Failed to save order:", err);
        }

        // show confirmation modal
        setConfirmationDetails({
          date: pickupDate,
          time: pickupTime,
          items: cart?.length || 0,
          subtotal: currency(totals.subtotal),
          deposit: currency(depositAmount),
          remaining: currency(remainingAmount),
          paymentMode,
        });
        setConfirmOpen(true);
      }
    } catch (err) {
      console.error("Payment failed:", err);
      setPaymentError(
        err?.message ||
          "Something went wrong while processing your payment. Please try again."
      );
    } finally {
      setIsPaying(false);
    }
  }

  const closeConfirmation = (clear = false) => {
    setConfirmOpen(false);
    if (clear) {
      clearCart();
      setPickupDate("");
      setPickupTime("");
      setPaymentSuccessMsg("");
    }
  };

  return (
    <div className="flex flex-col min-h-screen text-white bg-gradient-to-b from-gray-900 via-purple-900 to-black">
      <Navbar />

      <main className="flex-grow relative z-10 flex flex-col items-center justify-start pt-20 px-6">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8 shadow-lg w-full max-w-6xl flex flex-col md:flex-row gap-10">
          {/* Cart Items */}
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-pink-400 mb-4">Your Cart</h2>

            {!cart || cart.length === 0 ? (
              <p className="text-white/70">Your cart is empty.</p>
            ) : (
              <ul className="space-y-4">
                {cart.map((item, index) => {
                  const isPreset = item.shape === "Preset";
                  const itemCharms = flattenCharms(item.charms);
                  const perNailSummary = countCharmsByNail(item.charms);
                  return (
                    <li
                      key={index}
                      className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-black/30 rounded-lg p-4"
                    >
                      {/* Left side: Thumbnail + basic info */}
                      <div className="flex-1 flex items-start gap-4">
                        {isPreset && item.img && (
                          <img
                            src={item.img}
                            alt={item.name || "Preset Nail"}
                            className="w-24 h-24 object-contain rounded"
                          />
                        )}

                        <div className="min-w-0">
                          <p className="font-semibold truncate">
                            {isPreset
                              ? item.name || "Preset Design"
                              : `${item.shape} Nail`}
                          </p>
                          <p className="text-sm text-white/70">
                            {isPreset
                              ? "Pre-made design"
                              : `${item.length || "Medium"}, ${
                                  item.color || "Beige"
                                }`}
                          </p>

                          {/* charms preview tray */}
                          {itemCharms.length > 0 && (
                            <>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {itemCharms.slice(0, 8).map((c, i) => (
                                  <img
                                    key={i}
                                    src={c.src}
                                    alt="charm"
                                    className="w-6 h-6 rounded bg-white p-0.5"
                                  />
                                ))}
                                {itemCharms.length > 8 && (
                                  <span className="text-xs text-white/60">
                                    +{itemCharms.length - 8} more
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-white/60 mt-1">
                                Charms: {itemCharms.length} ({perNailSummary})
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Right side: price + actions */}
                      <div className="flex items-center justify-between md:justify-end gap-4 md:w-auto">
                        <p className="text-pink-400 font-bold">
                          ${currency(Number(item.price) || 0)}
                        </p>
                        <button
                          onClick={() => removeFromCart(index)}
                          className="text-sm text-red-400 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Checkout + payment */}
          <div className="w-full md:w-1/3 bg-black/40 rounded-lg p-6 shadow-lg h-max">
            <h3 className="text-xl font-semibold mb-4">Checkout Summary</h3>

            {/* 🔹 Payment mode toggle */}
            <div className="mb-4">
              <p className="text-sm text-white/80 mb-1">
                Choose how you want to pay:
              </p>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setPaymentMode("deposit")}
                  className={
                    "px-3 py-2 rounded-md text-sm border " +
                    (paymentMode === "deposit"
                      ? "bg-pink-500 border-pink-400 text-white"
                      : "bg-white/5 border-white/30 text-white/80 hover:bg-white/15")
                  }
                >
                  50% deposit now, rest at pickup
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMode("full")}
                  className={
                    "px-3 py-2 rounded-md text-sm border " +
                    (paymentMode === "full"
                      ? "bg-pink-500 border-pink-400 text-white"
                      : "bg-white/5 border-white/30 text-white/80 hover:bg-white/15")
                  }
                >
                  Pay full amount now
                </button>
              </div>
            </div>

            <div className="space-y-1 text-white/80">
              <p>Items: {cart?.length || 0}</p>
              <p>Charms: {totals.charmUnits}</p>
              <p className="border-t border-white/10 my-3"></p>
              <p>Subtotal: ${currency(totals.subtotal)}</p>
              <p>
                {paymentMode === "deposit" ? "50% Deposit:" : "Amount to charge now:"}{" "}
                ${currency(depositAmount)}
              </p>
              {remainingAmount > 0 && (
                <p className="text-sm text-white/70">
                  Remaining (Pay at Pickup): ${currency(remainingAmount)}
                </p>
              )}
            </div>

            {/* Pickup scheduling */}
            <div className="mt-5 space-y-4">
              <label className="block text-white/80 text-sm">
                Pickup Date{" "}
                <span className="text-white/50">
                  (on/after {minPickupDate})
                </span>
                <input
                  type="date"
                  className="mt-1 w-full px-3 py-2 rounded bg-white/10 border border-white/20 text-white"
                  min={minPickupDate}
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                />
              </label>

              {/* Fast time picker with clickable chips */}
              <div className="block text-white/80 text-sm">
                <div className="mb-1">
                  Pickup Time{" "}
                  <span className="text-white/50">(between 10:00–18:00)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {timeOptions.map((t) => {
                    const selected = pickupTime === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setPickupTime(t)}
                        className={
                          "px-3 py-1 rounded-full text-xs sm:text-sm border " +
                          (selected
                            ? "bg-pink-500 border-pink-400 text-white"
                            : "bg-white/5 border-white/30 text-white/80 hover:bg-white/15")
                        }
                      >
                        {formatTimeLabel(t)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {bookingError && (
                <div className="text-sm text-red-400">{bookingError}</div>
              )}
            </div>

            {/* Card input */}
            <div className="mt-5">
              <p className="text-sm text-white/80 mb-1">
                Pay securely by card:
              </p>
              <div className="bg-white rounded-md px-3 py-2">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#32325d",
                        "::placeholder": {
                          color: "#a0aec0",
                        },
                      },
                      invalid: {
                        color: "#e53e3e",
                      },
                    },
                  }}
                />
              </div>
            </div>

            {paymentError && (
              <div className="mt-2 text-sm text-red-400">{paymentError}</div>
            )}
            {paymentSuccessMsg && (
              <div className="mt-2 text-sm text-green-400">
                {paymentSuccessMsg}
              </div>
            )}

            <button
              onClick={handlePayDeposit}
              disabled={!cart || cart.length === 0 || isPaying}
              className="mt-4 w-full px-6 py-3 bg-pink-500 text-white rounded-lg shadow-lg hover:bg-pink-600 transition disabled:opacity-50"
            >
              {isPaying
                ? "Processing..."
                : paymentMode === "deposit"
                ? `Pay $${currency(depositAmount)} (50% deposit)`
                : `Pay $${currency(depositAmount)} (full amount)`}
            </button>

            {paymentMode === "deposit" && (
              <p className="text-xs text-white/60 mt-2">
                You’ll pay the remaining ${currency(remainingAmount)} in person
                at pickup.
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Confirmation Modal */}
      {confirmOpen && confirmationDetails && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-black rounded-xl shadow-lg p-8 w-full max-w-md relative">
            <button
              className="absolute top-3 right-3 text-2xl font-bold"
              onClick={() => closeConfirmation(false)}
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold mb-2">Order Confirmed ✅</h2>
            <p className="text-gray-700 mb-1">
              <strong>Pickup Date:</strong> {confirmationDetails.date}
            </p>
            <p className="text-gray-700 mb-1">
              <strong>Pickup Time:</strong>{" "}
              {formatTimeLabel(confirmationDetails.time)}
            </p>
            <p className="text-gray-700 mb-1">
              <strong>Items:</strong> {confirmationDetails.items}
            </p>
            <p className="text-gray-700 mb-1">
              <strong>Subtotal:</strong> ${confirmationDetails.subtotal}
            </p>
            <p className="text-gray-700 mb-1">
              <strong>
                {confirmationDetails.paymentMode === "deposit"
                  ? "Deposit Paid:"
                  : "Paid Now:"}
              </strong>{" "}
              ${confirmationDetails.deposit}
            </p>
            {confirmationDetails.remaining > 0 && (
              <p className="text-gray-700 mb-4">
                <strong>Remaining at Pickup:</strong> $
                {confirmationDetails.remaining}
              </p>
            )}

            <p className="text-pink-600 font-semibold mb-4">
              {confirmationDetails.paymentMode === "deposit"
                ? "Payment Method: 50% card deposit now, pay the rest at pickup."
                : "Payment Method: Paid in full online – nothing due at pickup."}
            </p>

            <div className="mt-4 flex gap-3">
              <button
                className="flex-1 bg-gray-200 text-gray-900 px-4 py-2 rounded-md hover:bg-gray-300 transition"
                onClick={() => closeConfirmation(false)}
              >
                Close
              </button>
              <button
                className="flex-1 bg-pink-500 text-white px-4 py-2 rounded-md hover:bg-pink-600 transition"
                onClick={() => closeConfirmation(true)}
              >
                Close & Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
